import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { formatToZMW, rootUrl, handlePrint } from "../../../helpers";
import { useAuth } from "../../../context/auth-context";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { FaBell, FaEye, FaPrint } from "react-icons/fa";
import PaymentVoucher from "../../../components/PaymentVoucher";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [isAuditor, setIsAuditor] = useState(false);
  const [hasApprovalRole, setHasApprovalRole] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cashId, setCashId] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [approvalTrail, setApprovalTrail] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [approvalTrailLoading, setApprovalTrailLoading] = useState(false);
  const [attachmentList, setAttachmentList] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [approved, setApproved] = useState("0");
  const [comment, setComment] = useState("");
  const auth = useAuth();
  const { refreshPendingApprovals } = useOutletContext();
  const voucherRef = useRef(null);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(rootUrl(`/getTrans/${auth.user.id}`));
      setTransactions(response.data.cash || []);
      setIsAuditor(Boolean(response.data.isAuditor));
      setHasApprovalRole(Boolean(response.data.hasApprovalRole));
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const statusLabel = (s) => {
    const n = Number(s);
    if (n === 0) return "1st approval";
    if (n === 1) return "2nd approval";
    if (n === 2) return "Final approval";
    return String(s);
  };

  const viewTransaction = async (row) => {
    setCashId(row.id);
    setSelectedRow(row);
    setShowModal(true);
    setComment("");
    setApproved("0");
    setApprovalTrail([]);
    setAuditTrail([]);
    setApprovalTrailLoading(true);
    setAttachmentsLoading(true);
    const attachmentIds = row.itemIds && row.itemIds.length > 0 ? row.itemIds : [row.id];

    try {
      const [attachmentResList, detailsRes] = await Promise.all([
        Promise.all(
          attachmentIds.map((id) => axios.get(rootUrl(`/getVoucherAttachments/${id}`)))
        ),
        axios.get(rootUrl(`/getDetails/${row.id}`)),
      ]);
      const mergedAttachments = attachmentResList
        .flatMap((res) => res.data?.data || [])
        .filter(
          (att, idx, arr) =>
            arr.findIndex((x) => x.id === att.id || x.filename === att.filename) === idx
        );
      setAttachmentList(mergedAttachments);
      setApprovalTrail(detailsRes.data?.cashApproval || []);
      setAuditTrail(detailsRes.data?.cashAudit || []);
    } catch (error) {
      setAttachmentList([]);
      setApprovalTrail([]);
      setAuditTrail([]);
      toast.error(error?.response?.data?.message || "Could not load voucher details");
    } finally {
      setAttachmentsLoading(false);
      setApprovalTrailLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setApprovalTrail([]);
    setAuditTrail([]);
    setApprovalTrailLoading(false);
    setAttachmentList([]);
    setAttachmentsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (approved === "9" && !comment.trim()) {
      toast.error("Please provide a comment before declining.");
      return;
    }

    const data = {
      approved,
      commForAct: comment,
      userFor: auth.user.id,
      cashId,
    };

    try {
      const response = await axios.post(rootUrl(`/postAction`), data);
      if (response.data.status === 200) {
        toast.success("Successful");
        fetchTransactions();
        refreshPendingApprovals?.();
        closeModal();
      } else {
        toast.error(response.data.msg || "Failed");
      }
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      toast.error(msg || "Failed");
    }
  };

  const handleAudit = async (e) => {
    e.preventDefault();
    const data = {
      userFor: auth.user.id,
      cashId,
      comment,
    };
    try {
      const response = await axios.post(rootUrl(`/auditPv`), data);
      if (response.data.status === 200) {
        toast.success("Voucher marked as audited");
        fetchTransactions();
        refreshPendingApprovals?.();
        closeModal();
      } else {
        toast.error(response.data.msg || "Failed");
      }
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      toast.error(msg || "Failed");
    }
  };

  const canApprove = Boolean(selectedRow?.canApprove);
  const canAudit = Boolean(selectedRow?.canAudit);
  const hasBothRoles = isAuditor && hasApprovalRole;
  const headingText = hasBothRoles
    ? "Pending voucher actions"
    : isAuditor
    ? "Pending vouchers for audit"
    : "Pending payment approvals";
  const subText = hasBothRoles
    ? "Vouchers you can act on (approve or audit depending on role and stage)."
    : isAuditor
    ? "Stage 1 and 2 vouchers you can review and mark as audited."
    : "Vouchers at your approval step (based on your approval role hierarchy).";
  const groupedTransactions = Object.values(
    transactions.reduce((acc, row) => {
      const key = row.voucher || `cash-${row.id}`;
      if (!acc[key]) {
        acc[key] = {
          ...row,
          amount: parseFloat(row.amount) || 0,
          itemIds: [row.id],
        };
      } else {
        acc[key].amount += parseFloat(row.amount) || 0;
        acc[key].itemIds.push(row.id);
        acc[key].canApprove = Boolean(acc[key].canApprove || row.canApprove);
        acc[key].canAudit = Boolean(acc[key].canAudit || row.canAudit);
        acc[key].isAudited = Boolean(acc[key].isAudited || row.isAudited);
      }
      return acc;
    }, {})
  );

  return (
    <div className="w-full">
      <div className="">
        <h4 className="text-[20px] text-slate-800 font-bold flex items-center">
          {" "}
          <FaBell className="mr-2" /> {headingText}
        </h4>
        <p className="text-sm text-gray-600 mt-1">
          {subText}
        </p>
        <hr className="my-3" />
        <div className="container mx-auto p-4 mt-3">
          {groupedTransactions.length === 0 ? (
            <p className="text-gray-500 text-sm py-6">Nothing pending your approval.</p>
          ) : (
            <table className="table-auto w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-blue-800 text-white">
                  <th className="border p-2">Sno</th>
                  <th className="border p-2">Voucher</th>
                  <th className="border p-2">Date</th>
                  <th className="border p-2">Description</th>
                  <th className="border p-2">Amount</th>
                  <th className="border p-2">Step</th>
                  <th className="border p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {groupedTransactions.map((transaction, index) => (
                  <tr key={transaction.id} className="text-left">
                    <td className="border p-2">{index + 1}</td>
                    <td className="border p-2">{transaction.voucher}</td>
                    <td className="border p-2">{transaction.date.substring(0, 10)}</td>
                    <td className="border p-2">{transaction.description}</td>
                    <td className="border p-2">{formatToZMW(parseFloat(transaction.amount) || 0)}</td>
                    <td className="border p-2 text-xs">{statusLabel(transaction.status)}</td>
                    <td className="border p-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-blue-800 hover:underline text-sm"
                        onClick={() => viewTransaction(transaction)}
                      >
                        <FaEye /> View details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && selectedRow && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center z-50 overflow-y-auto py-6 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-4">
            <div className="flex justify-between items-center px-4 py-3 border-b">
              <h2 className="text-xl font-semibold">Payment voucher</h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handlePrint(voucherRef)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-950 text-white rounded text-sm"
                >
                  <FaPrint /> Print
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-800 px-2"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <div ref={voucherRef} className="bg-white">
                <PaymentVoucher data={selectedRow} />
                <div className="border-t border-gray-200 pt-4 mt-6">
                  <p className="text-sm font-semibold text-gray-800 mb-3">Approval trail</p>
                  {approvalTrailLoading ? (
                    <p className="text-sm text-gray-500">Loading approval history...</p>
                  ) : approvalTrail.length > 0 ? (
                    <div className="space-y-2">
                      {approvalTrail.map((item) => {
                        const fullName = `${item.firstname || ""} ${item.lastname || ""}`.trim();
                        const message = item.comment?.trim() ? item.comment : "No comment";
                        return (
                          <div
                            key={item.id}
                            className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                          >
                            <p className="font-medium text-gray-800">
                              Step {Number(item.hierarchy) + 1} - {fullName || "Unknown approver"}
                            </p>
                            <p className="text-xs text-gray-500 mb-1">
                              {item.created_at?.substring(0, 19)?.replace("T", " ") || ""}
                            </p>
                            <p className="text-gray-700">{message}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No approvals recorded yet.</p>
                  )}
                </div>
                <div className="border-t border-gray-200 pt-4 mt-6">
                  <p className="text-sm font-semibold text-gray-800 mb-3">Audit trail</p>
                  {approvalTrailLoading ? (
                    <p className="text-sm text-gray-500">Loading audit history...</p>
                  ) : auditTrail.length > 0 ? (
                    <div className="space-y-2">
                      {auditTrail.map((item) => {
                        const fullName = `${item.firstname || ""} ${item.lastname || ""}`.trim();
                        const message = item.comment?.trim() ? item.comment : "No comment";
                        return (
                          <div
                            key={item.id}
                            className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                          >
                            <p className="font-medium text-gray-800">
                              {fullName || "Unknown auditor"}
                            </p>
                            <p className="text-xs text-gray-500 mb-1">
                              {item.created_at?.substring(0, 19)?.replace("T", " ") || ""}
                            </p>
                            <p className="text-gray-700">{message}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No audit recorded yet.</p>
                  )}
                </div>
                <div className="border-t border-gray-200 pt-4 mt-6">
                  <p className="text-sm font-semibold text-gray-800 mb-3">Attached PDFs</p>
                  {attachmentsLoading ? (
                    <p className="text-sm text-gray-500">Loading attached PDFs...</p>
                  ) : attachmentList.length > 0 ? (
                    attachmentList.map((a) => (
                      <div key={a.id} className="mb-6 last:mb-0">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <p
                            className="text-xs text-gray-500 truncate"
                            title={a.original_filename || a.filename}
                          >
                            {a.original_filename || a.filename}
                          </p>
                          <a
                            href={rootUrl(`/uploads/${a.filename}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-800 hover:underline whitespace-nowrap"
                          >
                            Open
                          </a>
                        </div>
                        <iframe
                          title={a.original_filename || "PDF"}
                          src={rootUrl(`/uploads/${a.filename}`)}
                          className="w-full min-h-[65vh] border border-gray-200 rounded-lg bg-gray-50"
                        />
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 italic">No PDF attachments for this voucher.</p>
                  )}
                </div>
              </div>
            </div>

            {canApprove && (
              <>
                <hr />
                <form onSubmit={handleSubmit} className="p-4 space-y-3">
                  <textarea
                    placeholder={approved === "9" ? "Comment (required for decline)" : "Comment (optional)"}
                    className="border p-3 text-sm w-full resize-none rounded"
                    rows="3"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <select
                    className="border p-2 text-sm w-full rounded"
                    value={approved}
                    onChange={(e) => setApproved(e.target.value)}
                  >
                    <option value="0">Approve this step (advance)</option>
                    <option value="9">Decline</option>
                  </select>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </>
            )}
            {canAudit && (
              <>
                <hr />
                <form onSubmit={handleAudit} className="p-4 space-y-3">
                  <textarea
                    placeholder="Audit comment (optional)"
                    className="border p-3 text-sm w-full resize-none rounded"
                    rows="3"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded"
                    >
                      Mark as audited
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
